/**
 * Client-facing framing copy for the AI Discoverability Benchmark report.
 *
 * Joan authored this wording (17-July-2026 notes, superseding 10-July) and it
 * is reproduced
 * VERBATIM — it is client-facing copy, not developer prose, so it is not
 * paraphrased or "improved" here. It lives in SHARED code, in git, for two
 * reasons:
 *
 *   1. The on-screen report and the Markdown deliverable render from the same
 *      constant, so the document and the screen can never disagree.
 *   2. It is framing, not analysis — unlike a report SECTION it carries no data
 *      and is not part of the per-client layout config Joan toggles. Changing it
 *      is a developer change, deployed, applied to every report at once.
 */
/** Joan's wording for the standard 25-prompt / 4-model shape. */
const METHODOLOGY_SHAPE_DEFAULT = 'Each stage includes five prompts, for a total of 25 prompts. Each prompt is tested across four selected AI models, creating up to 100 AI-generated prompt answers.';
/** Small numbers as words, matching Joan's register ("five prompts"). */
const NUMBER_WORDS = [
    'zero',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
];
function numberWord(n) {
    return NUMBER_WORDS[n] ?? String(n);
}
/**
 * Joan's methodology sentence, stated for the run that is actually being read.
 *
 * Her original hard-codes 25 prompts / four models / 100 answers. That is right
 * for the standard panel and WRONG for every other shape — 11 of 45 live runs
 * use a single-model panel, and a teaser uses five prompts. The report already
 * renders a derived denominator note under the visibility table, so a fixed
 * sentence here made the report contradict itself on those runs.
 *
 * Keeps her sentence structure and register (number words, "selected AI
 * models", "AI-generated prompt answers"). Falls back to her exact default when
 * the shape is unknown. When prompts do not divide evenly into stages, the
 * per-stage clause is dropped rather than rounded — a wrong "each stage
 * includes five" is worse than not saying it.
 */
export function methodologySentence(shape) {
    const prompts = shape?.promptCount ?? 0;
    const models = shape?.modelCount ?? 0;
    const stages = shape?.stageCount ?? 0;
    if (prompts <= 0 || models <= 0)
        return METHODOLOGY_SHAPE_DEFAULT;
    const answers = prompts * models;
    const modelClause = models === 1
        ? 'one selected AI model'
        : `${numberWord(models)} selected AI models`;
    const tail = `Each prompt is tested across ${modelClause}, creating up to ${answers} AI-generated prompt ${answers === 1 ? 'answer' : 'answers'}.`;
    const perStage = stages > 0 ? prompts / stages : 0;
    const evenlySplit = stages > 0 && prompts % stages === 0;
    const head = evenlySplit
        ? `Each stage includes ${numberWord(perStage)} ${perStage === 1 ? 'prompt' : 'prompts'}, for a total of ${prompts} ${prompts === 1 ? 'prompt' : 'prompts'}.`
        : stages > 0
            ? `The prompt set contains ${prompts} prompts across ${numberWord(stages)} buyer-journey stages.`
            : `The prompt set contains ${prompts} prompts.`;
    return `${head} ${tail}`;
}
/**
 * Joan's About copy for a specific run. Identical to `BENCHMARK_ABOUT` except
 * that the one sentence stating counts reflects the run being read. Both
 * renderers must call this rather than `BENCHMARK_ABOUT` whenever a run is in
 * hand, so page and document stay in step.
 */
export function benchmarkAbout(shape) {
    const sentence = methodologySentence(shape);
    if (sentence === METHODOLOGY_SHAPE_DEFAULT)
        return BENCHMARK_ABOUT;
    return BENCHMARK_ABOUT.map((block) => block.closing?.includes(METHODOLOGY_SHAPE_DEFAULT)
        ? {
            ...block,
            closing: block.closing.map((p) => p === METHODOLOGY_SHAPE_DEFAULT ? sentence : p),
        }
        : block);
}
export const BENCHMARK_ABOUT = [
    {
        heading: 'About the AI Discoverability Benchmark',
        paragraphs: [
            'The AI Discoverability Benchmark measures how a company appears in AI-generated answers to relevant scientific, workflow, and supplier-selection questions.',
            'Using prompts mapped to the buyer journey, the benchmark shows whether the company is visible, named, cited as a source, and/or recommended — and how that performance compares with named competitors and other companies AI surfaces.',
            'The report answers four core questions:',
        ],
        bullets: [
            'Are we visible when AI answers relevant buyer or scientist prompts?',
            'Who is AI surfacing instead of us?',
            'Where are we winning or losing across the buyer journey?',
            'Which sources and domains are cited in AI-generated answers?',
        ],
        closing: [
            'The benchmark helps marketing teams identify where AI visibility is strong, where competitors may be gaining ground, and which content, website, marketplace, publication, or media strategies may improve future visibility.',
        ],
    },
    {
        heading: 'How to Read the Metrics',
        paragraphs: [
            'Each scored AI answer is counted once for overall Visibility. Visibility means the company was present in the answer in any meaningful way.',
            'Mention, Citation, and Recommendation are separate signals and may overlap. For example, if one answer names a company, cites its website, and recommends it, that answer counts in all three signal columns but only once for overall Visibility.',
            'Because these signals overlap, do not add the columns across a row. Read down each column to compare companies by signal type.',
            'Source tables show which source types, domains, or journals were cited in the AI-generated prompt answers. “Answers Citing This Source” counts the number of scored answers that cited at least one source from that category or journal. “Total Citations” counts the total number of cited links.',
        ],
    },
    {
        heading: 'Methodology Detail',
        paragraphs: [
            'The benchmark uses a structured prompt set designed to reflect how scientists and buyers may use AI tools to research problems, evaluate workflows, compare technologies, identify suppliers, and make supplier-selection decisions.',
            'For this benchmark, prompts are organized across five buyer-journey stages:',
        ],
        bullets: [
            'Problem Definition',
            'Workflow Evaluation',
            'Technology Evaluation',
            'Supplier Evaluation',
            'Supplier Selection',
        ],
        closing: [
            // DEFAULT ONLY — the standard 25-prompt / 4-model shape, in Joan's exact
            // wording. Any caller that knows the run's real shape must render via
            // `benchmarkAbout(shape)` below instead, which substitutes the real
            // counts. 11 of 45 live runs use a single-model panel, where this
            // sentence would otherwise claim "four selected AI models ... 100
            // answers" directly above a derived note correctly saying "1 AI model —
            // 5 scored AI answers", i.e. the report would contradict itself.
            METHODOLOGY_SHAPE_DEFAULT,
            'Each model response is treated as one scored AI answer. Blank, failed, or unusable responses are marked as Not Scored and excluded from the denominator.',
            'Each scored answer is analyzed to determine whether the company was visible, mentioned, cited, and/or recommended. These signals are measured independently and may overlap. For example, one answer may name a company, cite its website, and recommend it; that answer would count in all three signal columns, but only once for overall Visibility.',
            'The same process is used for named competitors and additional companies surfaced by AI. The report also captures cited source types and domains, including supplier-owned websites, journals, directories, marketplaces, media brands, and other third-party resources.',
        ],
    },
];
/**
 * Per-section framing copy, Joan's 17-July wording VERBATIM.
 *
 * Same rule and the same reason as `BENCHMARK_ABOUT` above: BOTH the on-screen
 * report (`BenchmarkDetail.tsx`, `BenchmarkInsights.tsx`) and the Markdown
 * deliverable (`reportMarkdown.ts`) render from these constants, so the document
 * and the screen cannot disagree.
 *
 * This block exists because they DID disagree. Until 2026-07-19 each renderer
 * carried its own string literal for the same paragraph, and a live audit found
 * three silent drifts (a Top-Journals paragraph missing from the export, the
 * action-plan framing missing from the export, two different by-model intros)
 * plus one paragraph of Joan's that had never been applied at all. Duplicated
 * literals drift; one constant cannot.
 *
 * The "Use this data to:" label is supplied by each RENDERER (bold `<strong>` on
 * screen, `**bold**` in Markdown), so the strings below hold only the body.
 *
 * ⚠ Joan bans causal language here (17-July §7): no "relies on"/"relied on",
 * "most reliably accompany", "convert to recommendations", "significantly
 * under-retrieved", "shaping the research agenda", "back-loaded". "Appearance"
 * is not a metric name — the metric is Visibility, and the source-side column is
 * "Answers citing this source". The runtime banned-phrase detector in
 * `interpret.ts` only governs LLM-GENERATED commentary; hard-coded copy like
 * this is checked by hand.
 */
export const BENCHMARK_SECTION_COPY = {
    /** §2 AI Visibility Signal Summary (Joan 17-July, "Copy for Table"). */
    shareOfVoice: {
        intro: 'This table shows how often each company was present in the scored AI answers and how that presence was classified. Percentages reflect each company’s share of scored answers, not a share of the competitive total. Mention, Citation, and Recommendation are measured independently and may overlap.',
        useThisData: 'Understand whether AI is including your company in relevant answers, how your visibility compares with competitors, and which signals — mentions, citations, or recommendations — are driving that presence. Read down each column to compare companies by signal type; do not add signal columns across a row.',
    },
    /** §7 Source Types Shaping AI Answers (Joan 17-July §7). */
    citationSources: {
        intro: 'This table shows which source types were cited in the AI-generated prompt answers and how often they appeared in answers that also included a recommendation.',
        useThisData: 'Understand where AI is drawing source support from in this category — including supplier-owned content, peer-reviewed publications, directories and marketplaces, protocols, media brands, academic sources, and other third-party resources. These insights can help guide content, publication, directory, marketplace, and earned-media strategy.',
    },
    /** §9 Top Journals AI Cites (Joan 17-July §9). */
    topJournals: {
        intro: 'This table identifies peer-reviewed journals cited in the AI-generated prompt answers, including journals resolved from DOI, PubMed, PMC, publisher, and aggregator links where possible.',
        positioning: 'Aurora has built this report to go beyond raw citation domains. Where possible, the analysis resolves DOI, PubMed, PMC, publisher, and aggregator links to the underlying journal, turning raw AI citation data into more actionable publication and source intelligence.',
        whatTheTableShows: 'The table shows which journals were cited most often, how many scored prompt answers cited each journal, and how often those answers also included a recommendation.',
        useThisData: 'Understand which journals appear most often in AI-generated answers for this category. These insights can help guide article submission planning, prioritize journals for internal citation collection efforts, and identify opportunities to strengthen source-backed visibility over time.',
        methodologyNote: 'Journal resolution is based on available citation metadata, DOI redirects, publisher pages, PubMed/PMC records, and identifiable journal references. Some citations cannot be confidently resolved to a specific journal, so this table should be read as a high-confidence view of the journals cited most often in the AI-generated prompt answers, not a complete list of every cited journal.',
    },
    /**
     * §10 Top Source Types by AI Model. Aurora's own wording (Joan specified no
     * copy for this table), held to her §7 language rules: it reports what each
     * model CITED, and never asserts what a model "relied on" or was influenced
     * by — neither is observable from the outside.
     */
    sourcesByModel: {
        intro: 'This table shows the top three source types cited by each AI model in its benchmark answers. The percentage is that source type’s share of the model’s total cited source activity.',
        useThisData: 'Identify which source categories appear most often by model and across the full model set. Source types that rank highly across multiple models may represent priority channels for content, listing, publication, or source-building strategy.',
    },
    /** §11 Priority Action Plan (Joan 17-July §8, "Suggested section framing"). */
    actionPlan: {
        intro: 'This section highlights the five highest-priority actions based on the benchmark findings. Each action is tied to specific evidence from the report and prioritized by likely impact, effort, and timing.',
    },
};
//# sourceMappingURL=reportCopy.js.map